import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { useEffect, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";

// Custom marker icon (ink + champagne for luxe palette)
const customIcon = L.divIcon({
  className: "em-marker",
  html: `<div style="position:relative">
    <svg width="34" height="44" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 0C7.6 0 0 7.6 0 17c0 12.5 17 27 17 27s17-14.5 17-27C34 7.6 26.4 0 17 0z" fill="#18181A"/>
      <circle cx="17" cy="17" r="6" fill="#A07E45"/>
    </svg>
  </div>`,
  iconSize: [34, 44],
  iconAnchor: [17, 44],
  popupAnchor: [0, -38],
});

function FitToMarkers({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 11);
    } else {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
    }
  }, [points, map]);
  return null;
}

export default function MapView({ items, type = "event", height = "100%" }) {
  // Filter items with valid coords
  const points = useMemo(
    () => items.filter((i) => typeof i.lat === "number" && typeof i.lng === "number"),
    [items]
  );

  if (points.length === 0) {
    return (
      <div
        className="bg-cream-100 rounded-md flex items-center justify-center text-sm text-muted-foreground p-8"
        style={{ height }}
        data-testid="map-empty"
      >
        Aucune coordonnée disponible
      </div>
    );
  }

  const center = [points[0].lat, points[0].lng];

  return (
    <div
      data-testid="leaflet-map"
      className="rounded-md overflow-hidden border border-ink/10"
      style={{ height }}
    >
      <MapContainer
        center={center}
        zoom={6}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToMarkers points={points} />
        {points.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={customIcon}>
            <Popup>
              <div className="text-sm" style={{ minWidth: 180 }}>
                <Link
                  to={`/${type === "event" ? "events" : "venues"}/${p.id}`}
                  className="font-heading text-ink hover:text-accent text-base tracking-editorial"
                  data-testid={`map-popup-${p.id}`}
                >
                  {p.title || p.name}
                </Link>
                <p className="text-[11px] text-ink/50 mt-1 uppercase tracking-[0.15em]">{p.city}</p>
                {type === "event" && p.start_date && (
                  <p className="text-xs text-ink/55 mt-0.5">
                    {new Date(p.start_date).toLocaleDateString("fr-FR")}
                  </p>
                )}
                {type === "venue" && p.price_per_day && (
                  <p className="text-xs text-ink mt-1">
                    <span className="font-display italic">{p.price_per_day}</span>€ / jour
                  </p>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
